import React from 'react';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Spinner from './spinner/Spinner';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';

const schema = yup
  .object({
    course_id: yup.string().required('Course ID is required'),
    moduleFile: yup
      .mixed()
      .required('Please select a JSON file')
      .test('fileType', 'Please upload a valid JSON file', (value) => {
        if (!value || value.length === 0) {
          return false;
        }
        const file = value[0];
        return (
          file.type === 'application/json' ||
          file.name?.toLowerCase().endsWith('.json')
        );
      }),
  })
  .required();

function ImportCourseModule({ courseId, toggleModal }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      course_id: courseId,
    },
  });

  const queryClient = useQueryClient();

  const onImportSuccess = () => {
    toast.success('Module imported successfully!');
    toggleModal();
  };

  const onImportFailure = () => {
    toast.error("Couldn't import module! Please try again");
  };

  const { mutate, isLoading: isSubmitting } = useMutation(
    (data) => {
      // Let axios set multipart Content-Type with boundary automatically.
      return axiosInstancev2.post('/course-modules/import', data);
    },
    {
      onSuccess: () => {
        onImportSuccess();
        queryClient.invalidateQueries(['course-modules', courseId]);
      },
      onError: onImportFailure,
    }
  );

  const onSubmit = (data) => {
    const formData = new FormData();
    formData.append('courseId', courseId);
    formData.append('file', data.moduleFile[0]);
    mutate(formData);
  };

  return (
    <div>
      <form
        className="text-gray-700 space-y-2"
        onSubmit={handleSubmit(onSubmit)}
      >
        <input type="hidden" {...register('course_id')} />

        <div className="flex flex-col space-y-1">
          <label htmlFor="moduleFile" className="text-base">
            Module JSON File
          </label>
          <input
            {...register('moduleFile', { required: true })}
            accept="application/json,.json"
            type="file"
            id="moduleFile"
            className={`border-[#CBCBCB] border-[1px] px-2 py-2 rounded-lg focus:outline-none ${
              errors.moduleFile ? 'border-red-600' : ''
            }`}
          />
          {errors.moduleFile && (
            <span className="text-red-600 text-xs mt-2">
              {errors.moduleFile?.message}
            </span>
          )}
        </div>

        {isSubmitting ? (
          <div className="w-full flex align-middle justify-center items-center">
            <Spinner />
          </div>
        ) : (
          <div className="flex justify-between mt-8">
            <button
              className="border-[1px] border-claret-500 px-4 py-1 rounded-md text-claret-500 text-sm space-x-2 flex items-center"
              type="button"
              onClick={toggleModal}
            >
              <CancelIcon fontSize="inherit" />
              <p>Cancel</p>
            </button>
            <button
              className="px-4 py-1 rounded-md text-white text-sm space-x-2 flex items-center bg-claret-500"
              type="submit"
            >
              <SaveIcon fontSize="inherit" />
              <p>Import Module</p>
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

export default ImportCourseModule;
